import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, ArrowLeft, Coins, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { EmptyState, StatusBadge } from "@/components/brand";
import { MatchCard, MatchState, TeamBadge } from "@/components/SportsCards";
import { Skeleton } from "@/components/ui/skeleton";
import MatchLive from "@/components/MatchLive";
import ExchangeBoard from "@/components/ExchangeBoard";
import LiveScoreStrip from "@/components/LiveScoreStrip";
import { useAuth } from "@/context/AuthContext";

const TABS = [["live", "Live"], ["upcoming", "Upcoming"], ["finished", "Results"]];

const useMatches = (tab) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let off = false;
    const load = () => api.get("/cricket/matches", { params: { status: tab } }).then((r) => !off && setItems(r.data)).catch(() => !off && setItems([]));
    setItems(null);
    load();
    const t = tab === "live" ? setInterval(load, 30000) : null;
    return () => { off = true; if (t) clearInterval(t); };
  }, [tab]);
  return items;
};

export const MyPredictions = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { if (user?.role === "user") { api.get("/cricket/predictions/me").then((r) => setRows(r.data)).catch(() => setRows([])); } }, [user]);
  if (!rows || rows.length === 0) return null;
  return (
    <section data-testid="my-predictions" className="mt-12">
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold"><Trophy className="h-5 w-5 text-cr-gold" />My Predictions</h2>
      <div className="card-cr overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["Match", "Pick", "Stake", "Win", "Status", "Placed"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id} data-testid={`prediction-row-${p.id}`} className="border-t border-cr-line">
              <td className="px-4 py-3"><Link to={`/sports/match/${p.fixture_id}`} className="hover:text-cr-gold">{p.match}</Link></td>
              <td className="px-4 py-3 font-semibold">{p.team}</td><td className="px-4 py-3">{p.stake}</td>
              <td className="px-4 py-3 text-cr-gold">{p.status === "pending" ? p.potential : p.payout ?? 0}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status === "won" ? "completed" : p.status === "lost" ? "rejected" : p.status} testid={`prediction-status-${p.id}`} /></td>
              <td className="px-4 py-3 text-cr-muted">{dateTime(p.created_at)}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </section>
  );
};

export default function Sports({ initialStatus = "live" }) {
  const [tab, setTab] = useState(initialStatus);
  const [q, setQ] = useState("");
  const items = useMatches(tab);
  useEffect(() => { setTab(initialStatus); }, [initialStatus]);
  const s = q.toLowerCase();
  const shown = items?.filter((m) => !s || [m.home.name, m.away.name, m.league || ""].some((x) => x.toLowerCase().includes(s)));

  return (
    <main data-testid="sports-page" className="mx-auto max-w-7xl px-5 py-10">
      <div className="relative mb-8 overflow-hidden rounded-2xl border border-cr-gold/30">
        <img src="/brand/cricket/hero.jpg" alt="Cricket stadium" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/10" />
        <div className="relative flex flex-col gap-6 p-8 sm:p-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-cr-gold">Live scores · Predict & win</p>
            <h1 className="mt-3 text-4xl font-black uppercase sm:text-5xl lg:text-6xl">Live <span className="gold-text">Cricket</span></h1>
            <p className="mt-3 text-sm text-cr-ink/85 sm:text-base">Real-time scores from every major league. Predict the winner of upcoming matches with your coins and win 1.9x.</p>
          </div>
          <div className="relative w-full md:w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" />
            <input data-testid="sports-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams, leagues" className="input-cr pl-10" /></div>
        </div>
      </div>
      <div className="mb-8 flex gap-6 border-b border-cr-line">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`sports-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent text-cr-ink/75"}`}>{l}</button>
        ))}
      </div>
      {shown == null ? <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 bg-cr-elevated" />)}</div> :
        shown.length === 0 ? <EmptyState title={tab === "live" ? "No live matches right now" : "No matches found"} text="Check the Upcoming tab for the next fixtures." /> :
          <div data-testid="events-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{shown.map((m) => <MatchCard key={m.id} m={m} />)}</div>}
      <MyPredictions />
    </main>
  );
}

const PredictPanel = ({ m }) => {
  const { user, wallet, refreshWallet } = useAuth();
  const [pick, setPick] = useState("home");
  const [stake, setStake] = useState("100");
  const [busy, setBusy] = useState(false);
  if (!m.can_predict) return <p data-testid="predict-closed" className="card-cr p-5 text-sm text-cr-muted">Predictions are open only for upcoming matches with confirmed teams.</p>;
  if (!user) return <div className="card-cr p-5 text-sm">Please <Link to="/login" className="font-semibold text-cr-gold">log in</Link> to predict this match.</div>;
  if (user.role !== "user") return <p className="card-cr p-5 text-sm text-cr-muted">Only player accounts can predict.</p>;
  const submit = async () => {
    setBusy(true);
    try { await api.post("/cricket/predictions", { fixture_id: m.id, pick, stake: Number(stake) }); toast.success("Prediction placed"); refreshWallet(); setTimeout(() => window.location.reload(), 600); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="predict-panel" className="card-gold relative overflow-hidden p-6">
      <img src="/brand/cricket/trophy.jpg" alt="" className="pointer-events-none absolute -right-10 top-0 hidden h-full opacity-40 md:block" />
      <div className="relative md:max-w-[70%]">
      <h2 className="text-lg font-bold">Who will win?</h2>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {["home", "away"].map((k) => (
          <button key={k} data-testid={`predict-pick-${k}`} onClick={() => setPick(k)} className={`rounded-lg border p-4 text-left font-semibold transition-colors ${pick === k ? "border-cr-gold bg-cr-gold/10 text-cr-gold" : "border-cr-line hover:border-cr-gold/40"}`}>
            {m[k].name}<span className="block text-xs font-normal text-cr-muted">Pays {m.odds}x</span>
          </button>))}
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1"><Coins className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-gold" />
          <input data-testid="predict-stake-input" inputMode="numeric" value={stake} onChange={(e) => setStake(e.target.value.replace(/\D/g, ""))} className="input-cr pl-10" /></div>
        <button data-testid="predict-submit" disabled={busy || Number(stake) < 10} onClick={submit} className="btn-gold px-6 py-3 normal-case">{busy ? "Placing…" : `Predict · win ${Math.floor(Number(stake || 0) * m.odds)}`}</button>
      </div>
      <p className="mt-3 text-xs text-cr-muted">Your coins: <b data-testid="predict-coin-balance" className="text-cr-gold">{(wallet?.coins || 0).toLocaleString("en-IN")}</b> · Minimum 10 coins. Settled automatically when the result is in.</p>
      </div>
    </div>
  );
};

export function MatchDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const load = () => api.get(`/cricket/matches/${id}`).then((r) => setM(r.data)).catch(() => setErr(true));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [id]);
  if (err && !m) return <main className="mx-auto max-w-4xl px-5 py-16"><EmptyState title="Match not found" action={<Link to="/sports" className="btn-gold">Back to cricket</Link>} /></main>;
  if (!m) return <main className="mx-auto max-w-4xl px-5 py-16"><Skeleton className="h-80 bg-cr-elevated" /></main>;
  return (
    <main data-testid="match-detail-page" className="mx-auto max-w-5xl px-5 py-10">
      <Link to="/sports" data-testid="match-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-cr-gold"><ArrowLeft className="h-4 w-4" />All matches</Link>
      <LiveScoreStrip m={m} />
      <div className="card-gold relative overflow-hidden p-8">
        <img src="/brand/cricket/bowler.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-cr-muted">
            <span>{m.league}{m.venue ? ` · ${m.venue}` : ""}</span><span data-testid="match-status"><MatchState m={m} /></span>
          </div>
          <div className="mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-center">
            {["home", "away"].map((k, i) => (
              <div key={k} className={`flex flex-col items-center gap-3 ${i ? "order-3" : ""}`}>
                <TeamBadge team={m[k]} big />
                <p data-testid={`match-${k}`} className="font-display text-lg font-bold sm:text-2xl">{m[k].name}</p>
                <p data-testid={`match-${k}-score`} className="font-display text-xl font-black text-cr-gold">{m[k].score || "—"}</p>
              </div>))}
            <p className="order-2 font-display text-2xl font-black text-cr-muted">vs</p>
          </div>
          {m.result && <p data-testid="match-result" className="mt-6 text-center text-sm font-semibold text-cr-gold">{m.result}</p>}
        </div>
      </div>
      {m.scoreboards?.length > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">{m.scoreboards.map((s) => (
          <div key={s.id} className="card-cr flex items-center justify-between p-4 text-sm"><span className="font-semibold">{s.team_name}</span><span className="font-display text-cr-gold">{s.wickets} wkts · {s.overs} ov</span></div>))}
        </div>)}
      {m.state !== "finished" ? <ExchangeBoard fid={m.id} /> : <div className="mt-8"><PredictPanel m={m} /></div>}
      {m.can_predict && !m.ext && <Link to={`/fantasy/${m.id}`} data-testid="match-fantasy-link" className="btn-outline mt-4 w-full py-3 normal-case">Play fantasy for this match</Link>}
      {m.state !== "upcoming" && <MatchLive id={m.id} live={m.is_live} ext={m.ext} />}
    </main>
  );
}
